import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import { cn } from '@/shared/lib/utils';
import { useLanguageDirection } from '@/shared/hooks';
import { Button } from '@/shared/components/ui/Button';
import { Card, CardContent, CardHeader } from '@/shared/components/ui/Card';
import { ArrowLeftIcon, ArrowRightIcon, X } from 'lucide-react';
import { useWizard } from '../hooks/use-wizard';
import {
  WizardFlow as WizardFlowType,
  WizardState,
} from '../types/wizard.types';
import { SupportedLanguages } from '@/i18n/i18next';
import { WizardChatbotContainer } from './WizardChatbotContainer';
import { WizardStepProgress } from './WizardStepProgress';
import { WizardExitDialog } from './WizardExitDialog';
import { InteractionFormData, WizardAnswers } from '@momentum/types';

interface ReasonDetailsConfig<TFormData extends InteractionFormData> {
  title?: string;
  steps: (keyof TFormData)[];
  afterStep: keyof TFormData; // Step after which reason details should appear
}

interface WizardFlowProps<
  TFormData extends InteractionFormData = InteractionFormData
> {
  flow: WizardFlowType<TFormData>;
  initialLanguage?: SupportedLanguages;
  className?: string;
  showProgress?: boolean;
  showNavigation?: boolean;
  autoStart?: boolean;
  initialState?: Partial<WizardState>;
  // Step progress configuration
  reason?: ReasonDetailsConfig<TFormData>;
  firstStepId?: string;
}

export const WizardFlow = <
  TFormData extends InteractionFormData = InteractionFormData
>({
  flow,
  initialLanguage = 'en',
  className = '',
  showProgress = true,
  showNavigation = true,
  autoStart = true,
  initialState = {},
  reason,
  firstStepId,
}: WizardFlowProps<TFormData>) => {
  const { t } = useTranslation('wizard');
  const { isRTL } = useLanguageDirection();
  const navigate = useNavigate();
  const scrollToBottomRef = useRef<(() => void) | null>(null);
  const submitHandlerRef = useRef<(() => void) | null>(null);
  const prevStepIdRef = useRef<string | undefined>(undefined);
  const [showExitDialog, setShowExitDialog] = useState(false);

  const setSubmitHandler = useCallback((handler: (() => void) | null) => {
    submitHandlerRef.current = handler;
  }, []);

  const {
    currentStep,
    state,
    isCompleted,
    progress,
    canGoBack,
    startFlow,
    updateStep,
    answerStep,
    goToPreviousStep,
    reset,
  } = useWizard(
    flow as unknown as WizardFlowType<InteractionFormData>,
    initialLanguage,
    initialState,
    firstStepId
  );

  // Auto-start the flow if enabled
  useEffect(() => {
    if (autoStart && !state.currentStepId && !isCompleted) {
      startFlow();
    }

    window.addEventListener('beforeunload', () => reset());
  }, [autoStart, startFlow, state.currentStepId, isCompleted, reset]);

  // Handle step answer
  const handleStepAnswer = (answer: unknown) => {
    console.log('🎯 WizardFlow: Step answered:', answer);
    updateStep(answer);
  };

  // Handle next action (for manual navigation)
  const handleNext = (answer: unknown) => {
    console.log('➡️ WizardFlow: Manual next triggered');
    answerStep(answer);
    // This would typically be called from within the step component
  };

  // Handle previous action
  const handlePrevious = () => {
    console.log('⬅️ WizardFlow: Previous triggered');
    goToPreviousStep();
  };

  // Check if any actions have been made (answers provided)
  const hasActions = () => {
    return Object.keys(state.answers ?? {}).length > 0;
  };

  // Handle exit - clears wizard state and navigates back
  const handleExit = () => {
    reset(); // Clear wizard state
    setShowExitDialog(false);
    navigate({ to: '/' });
  };

  // Handle exit click - opens confirmation dialog only if actions were made
  const handleExitClick = () => {
    if (hasActions()) {
      // User has made changes, show confirmation dialog
      setShowExitDialog(true);
    } else {
      // No actions made, exit directly without confirmation
      handleExit();
    }
  };

  // If flow is completed, show success component or default success message
  if (isCompleted) {
    return (
      <div className={cn('w-full', className)} dir={isRTL ? 'rtl' : 'ltr'}>
        {flow.successComponent({
          answers: state.answers as WizardAnswers<TFormData>,
          onReset: reset,
        })}
      </div>
    );
  }

  // If no current step, show loading or start button
  if (!currentStep) {
    return <div>loading...</div>;
  }

  // Reset submit handler when step changes
  if (prevStepIdRef.current !== String(currentStep.id)) {
    submitHandlerRef.current = null;
    prevStepIdRef.current = String(currentStep.id);
  }

  // Create props for the current step component
  const stepProps = {
    value: state.answers[currentStep.id],
    onChange: handleStepAnswer,
    onNext: handleNext,
    onPrevious: handlePrevious,
    description: currentStep.description,
    error: state.error,
    disabled: false,
    isLoading: false,
    answers: state.answers, // Pass current answers for dynamic option resolution
    onScrollToBottom: () => {
      scrollToBottomRef.current?.();
    },
    // When Next is rendered in the card footer, pass setSubmitHandler so the
    // step can register its submit handler and hide its own Next button.
    ...(currentStep.showNextInFooter ? { setSubmitHandler } : {}),
  };

  // Render current step
  const StepComponent = currentStep.component;

  // Render chatbot mode
  if (currentStep.bot) {
    return (
      <div
        className={cn('w-full max-w-4xl mx-auto my-8', className)}
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        <div className="h-full rounded-2xl shadow-xl border border-gray-200 overflow-hidden bg-white">
          <WizardChatbotContainer
            messages={state.bot?.messages}
            groupTitle={currentStep.bot.groupTitle}
            currentInput={<StepComponent {...stepProps} />}
            className="h-full"
            scrollToBottomRef={scrollToBottomRef}
            onPrevious={handlePrevious}
            canGoBack={canGoBack}
            onExit={handleExitClick}
          />
        </div>

        {/* Debug information in development */}
        {process.env.NODE_ENV === 'development' &&
          (() => {
            try {
              const debugData = JSON.stringify(
                {
                  currentStepId: currentStep.id,
                  chatbotGroup: currentStep.bot?.group,
                  messagesCount: state.bot?.messages?.length || 0,
                  answers: state.answers,
                  progress,
                },
                null,
                2
              );
              return (
                <details className="mt-4 text-xs text-muted-foreground">
                  <summary>{t('common.debug.debugInfo')}</summary>
                  <pre className="mt-2 p-2 bg-muted rounded text-xs overflow-auto">
                    {debugData}
                  </pre>
                </details>
              );
            } catch (error) {
              console.log('Error stringifying debug data:', error);
              return null;
            }
          })()}

        <WizardExitDialog
          open={showExitDialog}
          onOpenChange={setShowExitDialog}
          onConfirm={handleExit}
        />
      </div>
    );
  }

  // Regular mode rendering
  return (
    <div
      className={cn('w-full h-full flex flex-col', className)}
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      <div className="flex flex-1">
        {/* Step Progress - Left Side */}
        {showProgress && (
          <div className="max-md:hidden flex-shrink-0 border-r border-border/50 bg-muted/20">
            <WizardStepProgress
              steps={flow.steps}
              currentStepId={String(currentStep.id)}
              answers={state.answers as WizardAnswers<TFormData>}
              reason={reason}
            />
          </div>
        )}

        {/* Main Content - Right Side */}
        <div className="flex-1 flex flex-col min-h-0 relative overflow-hidden">
          {/* Subtle animated gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-blue-100/20 to-blue-200/30 opacity-40" />
          <section className="flex-1 min-h-0 overflow-auto flex items-start p-4 2xl:mt-10">
            <Card className="w-full max-w-xl lg:max-w-2xl xl:max-w-4xl 2xl:max-w-5xl mx-auto bg-white/80 backdrop-blur-3xl border-0 shadow-lg shadow-primary/20 hover:shadow-primary/30 transition-all duration-500 rounded-2xl ring-1 ring-primary/5">
              <CardHeader className="pb-6 relative">
                <div className="flex justify-between">
                  <h2 className="text-3xl font-semibold text-gray-900 tracking-tight leading-tight">
                    {typeof currentStep.title === 'function'
                      ? currentStep.title(
                          state.answers as WizardAnswers<TFormData>
                        )
                      : currentStep.title}
                  </h2>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleExitClick}
                    className="end-4 h-8 w-8 rounded-full hover:bg-accent/50 transition-all duration-200"
                    aria-label={t('common.exit.exitWizard')}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                <p className="text-lg text-gray-600 leading-relaxed">
                  {currentStep.description || ''}
                </p>
              </CardHeader>
              <CardContent className="pt-0">
                <StepComponent {...stepProps} />
              </CardContent>
              {/* Card Navigation Footer — Previous, Next & Skip always here */}
              {showNavigation && (
                <div className="flex justify-between items-center px-6 py-4 border-t border-border/30">
                  <Button
                    variant="ghost"
                    onClick={handlePrevious}
                    disabled={!canGoBack}
                    className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-all duration-200 rounded-xl px-4 py-2"
                  >
                    {isRTL ? (
                      <ArrowRightIcon className="w-4 h-4" />
                    ) : (
                      <ArrowLeftIcon className="w-4 h-4" />
                    )}
                    {t('common.buttons.previous')}
                  </Button>
                  <div className="flex items-center gap-2">
                    {currentStep.skipable && (
                      <Button
                        variant="ghost"
                        onClick={() => answerStep(null)}
                        className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-all duration-200 rounded-xl px-4 py-2"
                      >
                        {t('common.buttons.skip')}
                      </Button>
                    )}
                    {currentStep.showNextInFooter && (
                      <Button
                        onClick={() => submitHandlerRef.current?.()}
                        className="flex items-center gap-2 transition-all duration-200 rounded-xl px-5 py-2"
                      >
                        {t('common.buttons.next')}
                        {isRTL ? (
                          <ArrowLeftIcon className="w-4 h-4" />
                        ) : (
                          <ArrowRightIcon className="w-4 h-4" />
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </Card>
          </section>
        </div>
      </div>

      {/* Debug information in development */}
      {process.env.NODE_ENV === 'development' &&
        (() => {
          try {
            const debugData = JSON.stringify(
              {
                currentStepId: currentStep.id,
                answers: state.answers,
                progress,
                canGoBack,
              },
              null,
              2
            );
            return (
              <details className="fixed top-30 end-20 text-xs text-muted-foreground">
                <summary>{t('common.debug.debugInfo')}</summary>
                <pre className="mt-2 p-2 bg-muted rounded text-xs overflow-auto">
                  {debugData}
                </pre>
              </details>
            );
          } catch (error) {
            console.log('Error stringifying debug data:', error);
            return null;
          }
        })()}

      <WizardExitDialog
        open={showExitDialog}
        onOpenChange={setShowExitDialog}
        onConfirm={handleExit}
      />
    </div>
  );
};
